<!--
  NotesPanel — G2.3 widget parity for admin's NotesPanelComponent.

  Collapsible section sits between Thread and Composer in ThreadPage.
  Header shows count + caret + "+ Add" when collapsed. Expanded body:
  newest-first list with two-click confirm trash, inline composer with
  Ctrl/Cmd+Enter shortcut, 4000-char counter, optimistic insert.

  Realtime: App.svelte invalidates ['conversations', convId, 'notes']
  on peer note frames; TanStack auto-refetches.
-->
<script lang="ts">
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { conversationNotes, type Note } from '../lib/api/queries.js';
  import { createNoteMutation, deleteNoteMutation } from '../lib/api/mutations.js';

  type Props = { conversationId: string };
  let { conversationId }: Props = $props();

  const queryClient = useQueryClient();
  // Re-derive on conversationId change so opening a different thread
  // swaps the query + mutation handles in lockstep.
  const q = $derived(createQuery(conversationNotes(conversationId)));
  const create = $derived(createMutation(createNoteMutation(queryClient, conversationId)));
  const remove = $derived(createMutation(deleteNoteMutation(queryClient, conversationId)));

  let expanded = $state(true);
  let draft = $state('');
  let errorMessage = $state<string | null>(null);
  let confirmDeleteId = $state<string | null>(null);
  let confirmTimer: ReturnType<typeof setTimeout> | null = null;

  // Reset transient state when the operator switches threads. Effect
  // tracks conversationId; runs at mount + every change.
  $effect(() => {
    conversationId;
    draft = '';
    errorMessage = null;
    confirmDeleteId = null;
    if (confirmTimer !== null) {
      clearTimeout(confirmTimer);
      confirmTimer = null;
    }
  });

  async function submit() {
    const body = draft.trim();
    if (body.length === 0 || $create.isPending) return;
    errorMessage = null;
    try {
      await $create.mutateAsync({ body });
      draft = '';
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : 'Unknown error.';
    }
  }

  function startDeleteConfirm(id: string) {
    confirmDeleteId = id;
    if (confirmTimer !== null) clearTimeout(confirmTimer);
    confirmTimer = setTimeout(() => {
      confirmDeleteId = null;
      confirmTimer = null;
    }, 3000);
  }

  async function commitDelete(noteId: string) {
    if (confirmTimer !== null) {
      clearTimeout(confirmTimer);
      confirmTimer = null;
    }
    confirmDeleteId = null;
    errorMessage = null;
    try {
      await $remove.mutateAsync({ noteId });
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : 'Unknown error.';
    }
  }

  function relative(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diff = Math.max(0, Date.now() - then);
    const min = 60_000;
    const hr = 60 * min;
    const day = 24 * hr;
    if (diff < min) return 'just now';
    if (diff < hr) return `${Math.floor(diff / min)}m ago`;
    if (diff < day) return `${Math.floor(diff / hr)}h ago`;
    if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function handleComposerKey(event: KeyboardEvent) {
    // Ctrl+Enter / Cmd+Enter shortcut. Plain Enter inserts a newline so
    // operators can write multi-line notes without accidental saves.
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submit();
    }
  }

  const notes = $derived<Note[]>($q.data ?? []);
  const count = $derived(notes.length);
  const saving = $derived($create.isPending);
</script>

<section class="notes" aria-label="Internal notes">
  <header class="notes-header">
    <button
      type="button"
      class="toggle"
      aria-expanded={expanded}
      aria-controls="widget-notes-body"
      onclick={() => (expanded = !expanded)}
    >
      <span class="caret" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
      <span class="label">Notes</span>
      {#if count > 0}
        <span class="count" aria-hidden="true">{count}</span>
      {/if}
    </button>
    {#if !expanded}
      <button
        type="button"
        class="add-link"
        aria-label="Add a new note"
        onclick={() => (expanded = true)}
      >+ Add</button>
    {/if}
  </header>

  {#if expanded}
    <div id="widget-notes-body" class="notes-body">
      {#if $q.isPending}
        <div class="empty" role="status">Loading notes...</div>
      {:else if $q.error}
        <div class="empty error-text" role="alert">Could not load notes.</div>
      {:else if notes.length === 0}
        <div class="empty">No notes yet.</div>
      {:else}
        <ul class="list" role="list">
          {#each notes as n (n.id)}
            <li class="row">
              <div class="row-meta">
                <strong class="author">{n.authorDisplayName ?? 'Operator'}</strong>
                <span class="time" title={n.createdAt}>{relative(n.createdAt)}</span>
              </div>
              <p class="body">{n.body}</p>
              {#if confirmDeleteId === n.id}
                <button
                  type="button"
                  class="confirm-btn"
                  onclick={() => void commitDelete(n.id)}
                >Confirm?</button>
              {:else}
                <button
                  type="button"
                  class="trash-btn"
                  aria-label={`Delete note from ${n.authorDisplayName ?? 'Operator'}`}
                  onclick={() => startDeleteConfirm(n.id)}
                >🗑</button>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}

      <form
        class="composer"
        onsubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <textarea
          class="composer-input"
          rows="2"
          maxlength="4000"
          bind:value={draft}
          placeholder="Add an internal note..."
          aria-label="New note body"
          disabled={saving}
          onkeydown={handleComposerKey}
        ></textarea>
        <div class="composer-row">
          <span class="composer-hint">{draft.length}/4000 · Ctrl+Enter to save</span>
          <button
            type="submit"
            class="save-btn"
            disabled={saving || draft.trim().length === 0}
            aria-busy={saving ? 'true' : null}
          >
            {saving ? 'Saving...' : 'Save note'}
          </button>
        </div>
      </form>

      {#if errorMessage}
        <div class="error" role="alert">{errorMessage}</div>
      {/if}
    </div>
  {/if}
</section>

<style>
  .notes {
    border-top: 1px solid var(--widget-divider, oklch(93% 0.006 250));
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    flex: 0 0 auto;
  }

  .notes-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 14px;
  }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 4px 0;
    font-family: var(--widget-font);
    font-size: 11.5px;
    font-weight: 600;
    color: var(--widget-fg, oklch(20% 0.01 250));
    cursor: pointer;
  }
  .toggle:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
    border-radius: 4px;
  }
  .caret {
    font-size: 10px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .label {
    letter-spacing: 0.03em;
  }
  .count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 16px;
    padding: 0 5px;
    border-radius: 999px;
    background: color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 14%, transparent);
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
    font-size: 10px;
    font-weight: 700;
    font-family: var(--widget-font-mono);
  }
  .add-link {
    background: none;
    border: none;
    padding: 0;
    font-size: 11.5px;
    font-weight: 500;
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
    cursor: pointer;
    text-decoration: underline;
    font-family: var(--widget-font);
  }
  .add-link:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .add-link:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 2px;
    border-radius: 2px;
  }

  .notes-body {
    padding: 0 14px 10px;
  }
  .empty {
    padding: 12px 0;
    text-align: center;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 12.5px;
  }
  .empty.error-text {
    color: var(--widget-danger, oklch(55% 0.2 25));
  }

  .list {
    list-style: none;
    margin: 0 0 10px;
    padding: 0;
    max-height: 220px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 4px 8px;
    padding: 8px 10px;
    background: var(--widget-bg-sunk, oklch(96% 0.006 250));
    border: 1px solid var(--widget-divider, oklch(93% 0.006 250));
    border-radius: 6px;
  }
  .row-meta {
    grid-column: 1;
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .author {
    font-size: 11.5px;
    font-weight: 700;
    color: var(--widget-fg, oklch(20% 0.01 250));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .time {
    font-family: var(--widget-font-mono);
    font-size: 10.5px;
    color: var(--widget-fg-dim, oklch(64% 0.01 250));
    white-space: nowrap;
  }
  .body {
    grid-column: 1 / -1;
    margin: 0;
    font-size: 12.5px;
    line-height: 1.45;
    color: var(--widget-fg, oklch(20% 0.01 250));
    white-space: pre-wrap;
    word-break: break-word;
  }
  .trash-btn,
  .confirm-btn {
    grid-column: 2;
    grid-row: 1;
    align-self: start;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: var(--widget-font);
  }
  .trash-btn {
    background: transparent;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    border: 1px solid transparent;
  }
  .trash-btn:hover {
    background: var(--widget-danger-tint, oklch(94% 0.04 25));
    color: var(--widget-danger, oklch(55% 0.2 25));
    border-color: var(--widget-danger, oklch(55% 0.2 25));
  }
  .trash-btn:focus-visible {
    outline: 2px solid var(--widget-danger, oklch(55% 0.2 25));
    outline-offset: 1px;
  }
  .confirm-btn {
    background: var(--widget-danger, oklch(55% 0.2 25));
    color: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    border: none;
  }
  .confirm-btn:hover {
    filter: brightness(1.06);
  }
  .confirm-btn:focus-visible {
    outline: 2px solid var(--widget-danger, oklch(55% 0.2 25));
    outline-offset: 2px;
  }

  .composer {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .composer-input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 6px;
    font-family: var(--widget-font);
    font-size: 12.5px;
    line-height: 1.45;
    color: var(--widget-fg, oklch(20% 0.01 250));
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    resize: vertical;
    min-height: 38px;
  }
  .composer-input:focus {
    outline: 3px solid color-mix(in oklch, var(--widget-accent, oklch(75% 0.165 75)) 22%, transparent);
    border-color: var(--widget-accent-deep, oklch(50% 0.19 65));
  }
  .composer-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .composer-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .composer-hint {
    font-size: 10.5px;
    color: var(--widget-fg-dim, oklch(64% 0.01 250));
    font-family: var(--widget-font-mono);
  }
  .save-btn {
    padding: 5px 12px;
    border-radius: 5px;
    background: var(--widget-accent, oklch(75% 0.165 75));
    color: var(--widget-fg, oklch(20% 0.012 250));
    font-size: 11.5px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    font-family: var(--widget-font);
  }
  .save-btn:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  .save-btn:focus-visible {
    outline: 2px solid var(--widget-accent-deep, oklch(50% 0.19 65));
    outline-offset: 2px;
  }
  .save-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .error {
    margin-top: 6px;
    padding: 6px 10px;
    border-radius: 5px;
    background: var(--widget-danger-tint, oklch(94% 0.04 25));
    color: var(--widget-danger, oklch(55% 0.2 25));
    font-size: 11.5px;
  }
</style>
